/**
 * IssueReporter.jsx — floating "Report issue" button mounted on every
 * authenticated page (App.jsx wires this in).
 *
 * Captures URL, route, user-agent, viewport, last 50 console logs, and
 * recent network errors automatically — so the super_admin doesn't have
 * to play "what page were you on / what did you click" detective.
 *
 * Backend: POST /api/issue-reports (any auth user can submit).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Bug, Send, X, Lightbulb, MessageSquare, HelpCircle } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 140.1 — tabs for the floating reporter so users can submit
// feature requests + comments through the SAME widget that already
// captures URL/console/network context.
// Iter 140.4 — added "Other / Not sure" tab for when reps are
// reporting an issue forwarded from someone else (client email, etc.)
// and may not know which page it's about or whether it's a bug.
const REPORTER_TABS = [
  {
    id: "bug",
    label: "Report a bug",
    icon: Bug,
    title: "Report a bug",
    subtitle: "Page URL, browser info, and recent console errors are attached automatically.",
    cta: "Submit bug",
    placeholder: 'e.g. "I clicked the # button on the dialer during a call and the keypad opened, but pressing 1-9 didn\'t send any tones."',
    fieldLabel: "What happened? What did you expect to happen?",
    showSeverity: true,
    showForwardedMeta: false,
    toastOnSuccess: "Thanks — bug reported. Joshua will follow up.",
  },
  {
    id: "feature",
    label: "Suggest an improvement",
    icon: Lightbulb,
    title: "Suggest an improvement",
    subtitle: "Tell us what you'd like to see — we'll add it to the product roadmap and let you know when it's planned.",
    cta: "Submit suggestion",
    placeholder: 'e.g. "It would help if the dialer could auto-pause new dials whenever I open Lead Focus during a live call."',
    fieldLabel: "What would you like us to improve, and why?",
    showSeverity: false,
    showForwardedMeta: false,
    toastOnSuccess: "Thanks — suggestion logged.",
  },
  {
    id: "comment",
    label: "Leave a comment",
    icon: MessageSquare,
    title: "Leave a comment",
    subtitle: "Share general feedback or questions — anything that's not specifically a bug or feature request.",
    cta: "Submit comment",
    placeholder: 'e.g. "The new segment builder feels much faster — nice change!"',
    fieldLabel: "What's on your mind?",
    showSeverity: false,
    showForwardedMeta: false,
    toastOnSuccess: "Thanks — your comment was sent.",
  },
  {
    id: "other",
    label: "Other / Not sure",
    icon: HelpCircle,
    title: "Forward or report something else",
    subtitle: "Use this when you're not sure if it's a bug or feature, or when you're reporting something a teammate or client emailed you about.",
    cta: "Submit",
    placeholder: 'Paste the email or describe what was reported to you. Include any clues about which page or feature is involved — even partial info is helpful.',
    fieldLabel: "What was reported?",
    showSeverity: false,
    showForwardedMeta: true,
    toastOnSuccess: "Thanks — forwarded to the team. Joshua will triage.",
  },
];

// ── Console + network capture (singleton, installed once on first mount) ──
const CONSOLE_BUFFER = [];
const NETWORK_BUFFER = [];
let CAPTURE_INSTALLED = false;

function installCapture() {
  if (CAPTURE_INSTALLED || typeof window === "undefined") return;
  CAPTURE_INSTALLED = true;
  ["log", "warn", "error", "info"].forEach((level) => {
    const orig = console[level].bind(console);
    console[level] = (...args) => {
      try {
        CONSOLE_BUFFER.push({
          level,
          ts: new Date().toISOString(),
          msg: args.map((a) => {
            try {
              return typeof a === "string" ? a : JSON.stringify(a);
            } catch {
              return String(a);
            }
          }).join(" ").slice(0, 500),
        });
        if (CONSOLE_BUFFER.length > 200) CONSOLE_BUFFER.shift();
      } catch { /* never break console */ }
      orig(...args);
    };
  });
  // Capture failed fetches + 4xx/5xx
  if (window.fetch) {
    const origFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const started = Date.now();
      try {
        const r = await origFetch(input, init);
        if (r.status >= 400) {
          NETWORK_BUFFER.push({
            url: String(typeof input === "string" ? input : (input?.url || "")).slice(0, 300),
            status: r.status,
            ms: Date.now() - started,
            ts: new Date().toISOString(),
          });
          if (NETWORK_BUFFER.length > 100) NETWORK_BUFFER.shift();
        }
        return r;
      } catch (e) {
        NETWORK_BUFFER.push({
          url: String(typeof input === "string" ? input : (input?.url || "")).slice(0, 300),
          error: String(e).slice(0, 200),
          ms: Date.now() - started,
          ts: new Date().toISOString(),
        });
        if (NETWORK_BUFFER.length > 100) NETWORK_BUFFER.shift();
        throw e;
      }
    };
  }
}

export default function IssueReporter({ inline = false }) {
  const { user, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [dockExpanded, setDockExpanded] = useState(false);
  const [kind, setKind] = useState("bug");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  // Iter 140.4 — extra fields available only on the "Other" tab so
  // reps can attribute a forwarded email and (optionally) name the
  // page / area when they know it. Both optional.
  const [forwardedFromEmail, setForwardedFromEmail] = useState("");
  const [subjectArea, setSubjectArea] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Iter 177.4 (Lisa #76669993 Feb 2026) — attachment support on the
  // floating reporter. Each file gets base64-encoded inline and shipped
  // with the POST payload. We mirror the email-composer caps (4 MB raw
  // per file, 8 MB aggregate after base64) so a single screenshot or
  // short MP4 fits comfortably and we don't blow the 16 MB Mongo doc
  // cap on the issue_reports collection.
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const activeTab = REPORTER_TABS.find((t) => t.id === kind) || REPORTER_TABS[0];

  useEffect(() => { installCapture(); }, []);
  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [open, kind]);

  // Iter 171.D (Danny — bug #5: bug icon disappears for Stephanie /
  // Himagna). The previous logic hid the FAB whenever the WebPhone
  // dock was MOUNTED — but the dock is mounted globally even when
  // collapsed (just visibility:hidden). Reps saw the bug icon for a
  // split second on refresh then it disappeared as soon as the dock
  // rendered. Now we check the dock's `data-expanded` attribute so
  // the FAB only hides when the softphone panel is actually visible.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const check = () => {
      const dock = document.querySelector('[data-testid="global-webphone-dock"]');
      const isExpanded = dock?.getAttribute("data-expanded") === "true";
      setDockExpanded(isExpanded);
    };
    check();
    const obs = new MutationObserver(check);
    // Watch for both child changes AND attribute flips on the dock.
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-expanded"] });
    return () => obs.disconnect();
  }, []);

  // Hide entirely when not logged in (login/landing pages).
  if (!user || !token) return null;

  const submit = async () => {
    if (description.trim().length < 4) {
      toast.error("Please add a sentence or two so we know what you mean");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        description: description.trim(),
        kind,
        severity: kind === "bug" ? severity : "low",
        url: window.location.href,
        route: window.location.pathname + window.location.search,
        user_agent: navigator.userAgent,
        viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
        console_logs: CONSOLE_BUFFER.slice(-50),
        network_errors: NETWORK_BUFFER.slice(-50),
        feature_flags: { /* room for runtime flags later */ },
      };
      // Iter 140.4 — "Other" tab: stash the forwarded-from email and
      // the rep's free-text guess at which page/area into `extra` so
      // the admin queue can surface them without schema migrations.
      if (kind === "other" && (forwardedFromEmail.trim() || subjectArea.trim())) {
        payload.extra = {
          forwarded_from_email: forwardedFromEmail.trim() || null,
          subject_area: subjectArea.trim() || null,
        };
      }
      // Iter 177.4 (Lisa #76669993) — include uploaded attachments.
      if (attachments.length > 0) {
        payload.attachments = attachments.map((a) => ({
          name: a.name, content_type: a.content_type, data_b64: a.data_b64, size: a.size,
        }));
      }
      await axios.post(`${API}/api/issue-reports`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(activeTab.toastOnSuccess);
      setDescription("");
      setSeverity("medium");
      setForwardedFromEmail("");
      setSubjectArea("");
      setAttachments([]);
      setKind("bug");
      setOpen(false);
    } catch (e) {
      toast.error("Couldn't submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  // Iter 177.4 (Lisa #76669993) — attachment picker handler. Encodes
  // each file as base64 and stores it in state until submit. Mirrors
  // the LeadFocus email-composer caps: 4 MB raw per file, 8 MB
  // aggregate.
  // Iter 180 — also called by the Ctrl+V paste handler with a synthetic
  // file list, so it must tolerate File-like blobs that may have empty
  // `.name` (clipboard images come through as `image/png` with no name).
  const onPickAttachments = async (fileList) => {
    const files = Array.from(fileList || []).map((f) => {
      if (f && f.name) return f;
      // Clipboard images arrive as Blob/File with no name → give them
      // a stable, descriptive default so the report list shows
      // something useful instead of "" or "image.png".
      const ext = (f?.type && f.type.split("/")[1]) || "png";
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      try {
        return new File([f], `pasted-screenshot-${ts}.${ext}`, { type: f.type || "image/png" });
      } catch {
        // File constructor not available — fall back to a wrapped blob
        // that exposes .name/.size/.type for the existing reader.
        return Object.assign(f, { name: `pasted-screenshot-${ts}.${ext}` });
      }
    });
    if (!files.length) return;
    const next = [];
    let aggregate = attachments.reduce((n, a) => n + (a.data_b64?.length || 0), 0);
    for (const f of files) {
      if (f.size > 4 * 1024 * 1024) {
        toast.error(`${f.name} is over 4 MB — please pick a smaller file or share via OneDrive.`);
        continue;
      }
      try {
        const buf = await f.arrayBuffer();
        // Iter 177.7 (Lisa #5b8ade41 Feb 2026) — chunk the byte→string
        // conversion. `String.fromCharCode(...uint8)` spreads every
        // byte as a function argument, which blows the JS arg-list
        // limit (Chromium caps at ~65k args) for anything larger
        // than ~64 KB. A 250 KB screenshot from the Windows Snipping
        // Tool throws "Maximum call stack size exceeded" → the
        // catch swallowed it as "Couldn't read screenshot.png" which
        // is exactly what Lisa reported. Build the binary string in
        // 32 KB slices and we never trip the limit.
        const bytes = new Uint8Array(buf);
        let bin = "";
        const CHUNK = 32 * 1024;
        for (let i = 0; i < bytes.length; i += CHUNK) {
          bin += String.fromCharCode.apply(
            null, bytes.subarray(i, Math.min(i + CHUNK, bytes.length)),
          );
        }
        const b64 = btoa(bin);
        if (aggregate + b64.length > 8 * 1024 * 1024) {
          toast.error("Total attachments exceed 8 MB — remove one and try again.");
          break;
        }
        aggregate += b64.length;
        next.push({
          name: f.name, size: f.size,
          content_type: f.type || "application/octet-stream",
          data_b64: b64,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[issue-reporter] failed to read", f.name, err);
        toast.error(`Couldn't read ${f.name} — ${err?.message || "unknown error"}.`);
      }
    }
    if (next.length) setAttachments((prev) => [...prev, ...next]);
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  // Iter 180 (Feb 2026) — Ctrl+V paste support. Lets reps snip a region
  // with Windows Snipping Tool / macOS screenshot-to-clipboard and
  // paste directly into the bug report instead of saving to disk and
  // then clicking Attach. Image items from `clipboardData.items` are
  // converted to File objects and routed through the same pipeline
  // as the file picker (so the 4 MB / 8 MB caps still apply).
  const onPasteCapture = (e) => {
    const items = e.clipboardData?.items;
    if (!items || !items.length) return;
    const imageFiles = [];
    for (const it of items) {
      if (it.kind === "file" && it.type && it.type.startsWith("image/")) {
        const blob = it.getAsFile();
        if (blob) imageFiles.push(blob);
      }
    }
    if (imageFiles.length) {
      e.preventDefault();
      onPickAttachments(imageFiles);
      toast.success(
        imageFiles.length === 1
          ? "Screenshot pasted — it'll be attached to the report."
          : `${imageFiles.length} screenshots pasted.`,
      );
    }
  };

  return (
    <>
      {/* Trigger button. In `inline` mode it renders as a normal dock icon
          (so it lives inside the collapsible bottom dock); otherwise it's a
          standalone floating FAB stacked above the WebPhone dock. */}
      {inline ? (
        <button
          type="button"
          data-testid="issue-reporter-fab"
          aria-label="Report an issue"
          onClick={() => setOpen(true)}
          style={{
            flexShrink: 0,
            width: 32, height: 32, borderRadius: "50%",
            background: "var(--navy, #0a2540)", color: "white",
            border: 0, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            transition: "transform 0.15s, opacity 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.08)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          title="Send feedback — report a bug, request a feature, or leave a comment"
        >
          <Bug style={{ width: 14, height: 14 }} />
        </button>
      ) : (
      <button
        type="button"
        data-testid="issue-reporter-fab"
        aria-label="Report an issue"
        onClick={() => setOpen(true)}
        style={{
          position: "fixed", right: 18,
          // Iter 175.3 (Cluster A — Tatiana's `904f2631`): the FAB used
          // to be HIDDEN whenever the WebPhone dock was expanded —
          // which for reps who keep the softphone open all day during
          // dialing sessions meant the bug button effectively never
          // showed up. Now we reposition it ABOVE the expanded dock
          // (bottom: 340 lifts it clear of the ~300px dock height)
          // instead of hiding entirely. Always reachable; never
          // overlapping the dock.
          bottom: dockExpanded ? 340 : 84,
          zIndex: 9990,
          width: 32, height: 32, borderRadius: 999,
          background: "var(--navy, #0a2540)", color: "white",
          border: 0, cursor: "pointer", boxShadow: "0 4px 12px rgba(10,37,64,0.20)",
          display: open ? "none" : "inline-flex",
          alignItems: "center", justifyContent: "center",
          opacity: 0.55, transition: "opacity 0.15s, transform 0.15s, bottom 0.2s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.transform = "scale(1.08)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.55; e.currentTarget.style.transform = "scale(1)"; }}
        title="Send feedback — report a bug, request a feature, or leave a comment"
      >
        <Bug style={{ width: 14, height: 14 }} />
      </button>
      )}

      {/* Modal — portaled to <body> so the bottom-dock's CSS transform
          (transform: translateY/translateX on the dock containers) does
          NOT become the containing block for this position:fixed overlay.
          Without the portal the modal resolved against the narrow dock and
          was crushed into a ~250px distorted strip. */}
      {open && createPortal(
        <div
          role="dialog"
          aria-label="Send feedback"
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(10,37,64,0.32)", display: "flex",
            alignItems: "center", justifyContent: "center", padding: 16,
          }}
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <div
            data-testid="issue-reporter-modal"
            onPaste={onPasteCapture}
            style={{
              background: "white", borderRadius: 12, width: "100%", maxWidth: 560,
              padding: "20px 22px", boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
              border: "0.5px solid var(--border)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 650, margin: 0 }}>{activeTab.title}</h2>
                <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: "4px 0 0 0" }}>
                  {activeTab.subtitle}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ background: "none", border: 0, cursor: "pointer", color: "var(--text-tertiary)", padding: 4 }}
                aria-label="Close"
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {/* Tab bar — Bug / Feature / Comment */}
            <div
              role="tablist"
              data-testid="issue-reporter-tabs"
              style={{
                display: "flex", gap: 4, padding: 3, background: "var(--bg-secondary, #f4f6fa)",
                borderRadius: 8, marginBottom: 14,
              }}
            >
              {REPORTER_TABS.map((t) => {
                const Icon = t.icon;
                const isActive = kind === t.id;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`issue-reporter-tab-${t.id}`}
                    onClick={() => setKind(t.id)}
                    style={{
                      flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                      padding: "7px 10px", border: 0, borderRadius: 6, cursor: "pointer",
                      background: isActive ? "white" : "transparent",
                      color: isActive ? "var(--navy, #0a2540)" : "var(--text-secondary)",
                      fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                      boxShadow: isActive ? "0 1px 3px rgba(10,37,64,0.10)" : "none",
                      transition: "background 0.12s, color 0.12s",
                    }}
                  >
                    <Icon style={{ width: 13, height: 13 }} />
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Iter 180 (Feb 2026) — attachment block lifted ABOVE the
                description so it's the first thing reps see after
                picking a tab. Includes a clear Ctrl+V hint. */}
            <div data-testid="issue-reporter-attachments" style={{ marginBottom: 14 }}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { onPickAttachments(e.target.files); e.target.value = ""; }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  data-testid="issue-reporter-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting || attachments.length >= 8}
                  style={{
                    padding: "8px 14px",
                    border: "1px dashed var(--blue, #2563eb)",
                    background: "rgba(37,99,235,0.06)",
                    borderRadius: 8, fontSize: 12, fontWeight: 600,
                    color: "var(--blue, #2563eb)",
                    cursor: attachments.length >= 8 ? "not-allowed" : "pointer",
                    fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  📎 Attach screenshot or file
                </button>
                <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                  or press <kbd style={{
                    padding: "1px 5px", border: "0.5px solid var(--border)", borderRadius: 3,
                    background: "var(--page-bg, #f8fafc)", fontSize: 10, fontFamily: "ui-monospace, monospace",
                    color: "var(--text-secondary)",
                  }}>Ctrl</kbd>+<kbd style={{
                    padding: "1px 5px", border: "0.5px solid var(--border)", borderRadius: 3,
                    background: "var(--page-bg, #f8fafc)", fontSize: 10, fontFamily: "ui-monospace, monospace",
                    color: "var(--text-secondary)",
                  }}>V</kbd> to paste a screenshot · 4 MB each, 8 MB total
                </span>
              </div>
              {attachments.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                  {attachments.map((a, i) => (
                    <div
                      key={`${a.name}-${i}`}
                      data-testid={`issue-reporter-attachment-${i}`}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "4px 8px", background: "var(--page-bg, #f8fafc)",
                        border: "0.5px solid var(--border)", borderRadius: 4,
                      }}
                    >
                      <span style={{ fontSize: 11, color: "var(--text-secondary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.name}
                      </span>
                      <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                        {a.size ? `${(a.size / 1024).toFixed(0)} KB` : ""}
                      </span>
                      <button
                        type="button"
                        data-testid={`issue-reporter-remove-${i}`}
                        onClick={() => removeAttachment(i)}
                        disabled={submitting}
                        style={{ background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", padding: "2px 6px", fontSize: 14 }}
                        aria-label={`Remove ${a.name}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>
              {activeTab.fieldLabel}
            </label>
            <textarea
              ref={textareaRef}
              data-testid="issue-reporter-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={activeTab.placeholder}
              rows={5}
              style={{
                width: "100%", padding: "10px 12px", border: "0.5px solid var(--border)",
                borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical",
                color: "var(--text-primary)",
              }}
            />

            {activeTab.showForwardedMeta && (
              <div data-testid="issue-reporter-forwarded-meta" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 4, color: "var(--text-secondary)" }}>
                    Forwarded from (optional)
                  </label>
                  <input
                    type="email"
                    data-testid="issue-reporter-forwarded-from"
                    value={forwardedFromEmail}
                    onChange={(e) => setForwardedFromEmail(e.target.value)}
                    placeholder="client@example.com"
                    style={{
                      width: "100%", padding: "7px 10px", border: "0.5px solid var(--border)",
                      borderRadius: 6, fontSize: 12, fontFamily: "inherit", color: "var(--text-primary)",
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 4, color: "var(--text-secondary)" }}>
                    Page / area (if you know)
                  </label>
                  <input
                    type="text"
                    data-testid="issue-reporter-subject-area"
                    value={subjectArea}
                    onChange={(e) => setSubjectArea(e.target.value)}
                    placeholder='e.g. "Dialer", "Lead Focus", "not sure"'
                    style={{
                      width: "100%", padding: "7px 10px", border: "0.5px solid var(--border)",
                      borderRadius: 6, fontSize: 12, fontFamily: "inherit", color: "var(--text-primary)",
                    }}
                  />
                </div>
              </div>
            )}

            {activeTab.showSeverity && (
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>
                  Severity
                </label>
                <select
                  data-testid="issue-reporter-severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                  style={{
                    padding: "6px 10px", border: "0.5px solid var(--border)",
                    borderRadius: 6, fontSize: 12, background: "white", color: "var(--text-primary)",
                    fontFamily: "inherit",
                  }}
                >
                  <option value="low">Low — minor / cosmetic</option>
                  <option value="medium">Medium — annoying but workable</option>
                  <option value="high">High — blocks me from doing my job</option>
                  <option value="blocker">Blocker — nobody can use the feature</option>
                </select>
              </div>
            )}

            {/* Attachment block lives ABOVE the description (Iter 180,
                Feb 2026) — moved up so reps actually notice the option
                to attach screenshots / Ctrl+V paste them. */}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={submitting}
                style={{
                  padding: "8px 14px", border: "0.5px solid var(--border)",
                  background: "white", borderRadius: 8, fontSize: 12, fontWeight: 600,
                  color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="issue-reporter-submit"
                onClick={submit}
                disabled={submitting || description.trim().length < 4}
                style={{
                  padding: "8px 14px", border: 0, background: "var(--blue, #2563eb)",
                  color: "white", borderRadius: 8, fontSize: 12, fontWeight: 600,
                  cursor: submitting ? "wait" : "pointer", display: "inline-flex",
                  alignItems: "center", gap: 6, opacity: submitting || description.trim().length < 4 ? 0.6 : 1,
                  fontFamily: "inherit",
                }}
              >
                <Send style={{ width: 12, height: 12 }} />
                {submitting ? "Sending…" : activeTab.cta}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
